<script>
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useAgentsList } from 'dashboard/composables/useAgentsList';

import ThumbnailGroup from 'dashboard/components/widgets/ThumbnailGroup.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import InternalChatAPI from 'dashboard/api/internalChat';
import ConversationAPI from 'dashboard/api/conversations';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

export default {
  components: {
    Spinner,
    ThumbnailGroup,
    MultiselectDropdownItems,
    NextButton,
    Avatar,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { agentsList } = useAgentsList(false);
    return {
      agentsList,
    };
  },
  data() {
    return {
      selectedWatchers: [],
      showDropDown: false,
      privateConversationAgentId: null,
      whatsappConversationPhone: null,
      groupParticipants: [],
      isLoadingGroupParticipants: false,
      groupParticipantsError: false,
    };
  },
  computed: {
    ...mapGetters({
      watchersUiFlas: 'conversationWatchers/getUIFlags',
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
    }),
    watchersFromStore() {
      return this.$store.getters['conversationWatchers/getByConversationId'](
        this.conversationId
      );
    },
    isInternalTeamChat() {
      return !!this.currentChat?.additional_attributes?.internal_team_chat;
    },
    isWhatsappGroup() {
      return this.currentChat?.meta?.sender?.identifier?.endsWith('@g.us');
    },
    whatsappGroupParticipants() {
      return this.groupParticipants;
    },
    watchersList: {
      get() {
        return this.selectedWatchers;
      },
      set(participants) {
        this.selectedWatchers = [...participants];
        const userIds = participants.map(el => el.id);
        this.updateParticipant(userIds);
      },
    },
    isUserWatching() {
      return this.selectedWatchers.some(
        watcher => watcher.id === this.currentUser.id
      );
    },
    thumbnailList() {
      return this.selectedWatchers.slice(0, 4);
    },
    moreAgentCount() {
      const maxThumbnailCount = 4;
      return this.watchersList.length - maxThumbnailCount;
    },
    moreThumbnailsText() {
      if (this.moreAgentCount > 1) {
        return this.$t('CONVERSATION_PARTICIPANTS.REMANING_PARTICIPANTS_TEXT', {
          count: this.moreAgentCount,
        });
      }
      return this.$t('CONVERSATION_PARTICIPANTS.REMANING_PARTICIPANT_TEXT', {
        count: 1,
      });
    },
    showMoreThumbs() {
      return this.moreAgentCount > 0;
    },
    totalWatchersText() {
      if (this.selectedWatchers.length > 1) {
        return this.$t('CONVERSATION_PARTICIPANTS.TOTAL_PARTICIPANTS_TEXT', {
          count: this.selectedWatchers.length,
        });
      }
      return this.$t('CONVERSATION_PARTICIPANTS.TOTAL_PARTICIPANT_TEXT', {
        count: 1,
      });
    },
  },
  watch: {
    conversationId() {
      if (this.isWhatsappGroup) this.fetchWhatsappGroupParticipants();
      else this.fetchParticipants();
    },
    watchersFromStore(participants = []) {
      this.selectedWatchers = [...participants];
    },
  },
  mounted() {
    if (this.isWhatsappGroup) this.fetchWhatsappGroupParticipants();
    else this.fetchParticipants();
    this.$store.dispatch('agents/get');
  },
  methods: {
    async fetchWhatsappGroupParticipants() {
      this.isLoadingGroupParticipants = true;
      this.groupParticipantsError = false;
      try {
        const { data } = await ConversationAPI.getWhatsappGroupParticipants(
          this.conversationId
        );
        this.groupParticipants = data.participants || [];
      } catch (error) {
        this.groupParticipants = [];
        this.groupParticipantsError = true;
      } finally {
        this.isLoadingGroupParticipants = false;
      }
    },
    fetchParticipants() {
      const conversationId = this.conversationId;
      this.$store.dispatch('conversationWatchers/show', { conversationId });
    },
    async updateParticipant(userIds) {
      const conversationId = this.conversationId;
      let alertMessage = this.$t(
        'CONVERSATION_PARTICIPANTS.API.SUCCESS_MESSAGE'
      );

      try {
        await this.$store.dispatch('conversationWatchers/update', {
          conversationId,
          userIds,
        });
      } catch (error) {
        alertMessage =
          error?.message ||
          this.$t('CONVERSATION_PARTICIPANTS.API.ERROR_MESSAGE');
      } finally {
        useAlert(alertMessage);
      }
      this.fetchParticipants();
    },
    onOpenDropdown() {
      this.showDropDown = true;
    },
    onCloseDropdown() {
      this.showDropDown = false;
    },
    onClickItem(agent) {
      const isAgentSelected = this.watchersList.some(
        participant => participant.id === agent.id
      );

      if (isAgentSelected) {
        const updatedList = this.watchersList.filter(
          participant => participant.id !== agent.id
        );

        this.watchersList = [...updatedList];
      } else {
        this.watchersList = [...this.watchersList, agent];
      }
    },
    onSelfAssign() {
      this.watchersList = [...this.selectedWatchers, this.currentUser];
    },
    async openPrivateConversation(agent) {
      const roomId =
        this.currentChat?.additional_attributes?.internal_chat_room_id;
      if (!roomId || this.privateConversationAgentId) return;

      this.privateConversationAgentId = agent.id;
      try {
        const { data } = await InternalChatAPI.startPrivateConversation(
          roomId,
          agent.id
        );
        await this.$store.dispatch('getConversation', data.conversation_id);
        await this.$router.push({
          name: 'conversations_through_team',
          params: {
            accountId: this.$route.params.accountId,
            teamId: this.$route.params.teamId,
            conversationId: data.conversation_id,
          },
        });
      } catch (error) {
        useAlert(this.$t('INTERNAL_CHAT.PRIVATE_CONVERSATION_ERROR'));
      } finally {
        this.privateConversationAgentId = null;
      }
    },
    participantPhone(participant) {
      return String(
        participant.phone_number || participant.phoneNumber || ''
      ).replace(/\D/g, '');
    },
    participantName(participant) {
      return participant.saved_name || this.participantPhone(participant);
    },
    async startWhatsappConversation(participant) {
      const phoneNumber = this.participantPhone(participant);
      if (!phoneNumber || this.whatsappConversationPhone) return;

      this.whatsappConversationPhone = phoneNumber;
      try {
        const { data } = await ConversationAPI.startWhatsappConversation(
          this.conversationId,
          phoneNumber
        );
        if (!data.conversation_id) {
          if (data.contact_already_saved) {
            useAlert(this.$t('INTERNAL_CHAT.GROUP_CONTACT_ALREADY_EXISTS'));
          }
          await this.$router.push({
            name: 'contacts_edit',
            params: {
              accountId: this.accountId,
              contactId: data.contact_id,
            },
            query: {
              compose: 'true',
              inbox_id: data.inbox_id,
              return_to_conversation: frontendURL(
                conversationUrl({
                  accountId: this.accountId,
                  activeInbox: this.currentChat.inbox_id,
                  id: this.conversationId,
                })
              ),
            },
          });
          return;
        }
        await this.$store.dispatch('getConversation', data.conversation_id);
        await this.$router.push({
          path: frontendURL(
            conversationUrl({
              accountId: this.accountId,
              activeInbox: this.currentChat.inbox_id,
              id: data.conversation_id,
            })
          ),
        });
      } catch (error) {
        useAlert(this.$t('INTERNAL_CHAT.GROUP_START_ERROR'));
      } finally {
        this.whatsappConversationPhone = null;
      }
    },
  },
};
</script>

<template>
  <div class="relative">
    <div v-if="isInternalTeamChat" class="flex flex-col gap-2">
      <p v-if="!watchersList.length" class="m-0 text-sm text-n-slate-10">
        {{ $t('INTERNAL_CHAT.NO_MEMBERS') }}
      </p>
      <div
        v-for="agent in watchersList"
        :key="agent.id"
        class="flex items-center gap-2 rounded-lg border border-n-weak p-2"
      >
        <Avatar
          :name="agent.name"
          :src="agent.thumbnail || agent.avatar_url"
          :size="28"
        />
        <div class="min-w-0 flex-1">
          <p class="m-0 truncate text-sm font-medium text-n-slate-12">
            {{ agent.name }}
          </p>
          <p class="m-0 truncate text-xs text-n-slate-10">
            {{ agent.email }}
          </p>
        </div>
        <NextButton
          v-if="agent.id !== currentUser.id"
          v-tooltip.left="$t('INTERNAL_CHAT.PRIVATE_MESSAGE')"
          ghost
          slate
          sm
          icon="i-lucide-message-circle"
          :label="$t('INTERNAL_CHAT.PRIVATE')"
          :is-loading="privateConversationAgentId === agent.id"
          :disabled="!!privateConversationAgentId"
          @click="openPrivateConversation(agent)"
        />
      </div>
    </div>
    <div v-else-if="isWhatsappGroup" class="flex flex-col gap-2">
      <div v-if="isLoadingGroupParticipants" class="flex justify-center py-4">
        <Spinner size="small" />
      </div>
      <p v-else-if="groupParticipantsError" class="m-0 text-sm text-n-ruby-11">
        {{ $t('INTERNAL_CHAT.GROUP_LOAD_ERROR') }}
      </p>
      <p
        v-else-if="!whatsappGroupParticipants.length"
        class="m-0 text-sm text-n-slate-10"
      >
        {{ $t('INTERNAL_CHAT.GROUP_EMPTY') }}
      </p>
      <div
        v-for="participant in whatsappGroupParticipants"
        :key="participant.jid || participant.id"
        class="flex items-center gap-2 rounded-lg border border-n-weak p-2"
      >
        <Avatar
          :name="participantName(participant)"
          :src="participant.img_url || participant.imgUrl"
          :size="28"
        />
        <div class="min-w-0 flex-1">
          <p class="m-0 truncate text-sm font-medium text-n-slate-12">
            {{ participantName(participant) }}
          </p>
          <p class="m-0 truncate text-xs text-n-slate-10">
            +{{ participantPhone(participant) }}
            <span v-if="participant.admin">
              · {{ $t('INTERNAL_CHAT.GROUP_ADMIN') }}
            </span>
          </p>
        </div>
        <NextButton
          v-tooltip.left="$t('INTERNAL_CHAT.GROUP_START')"
          ghost
          slate
          sm
          icon="i-lucide-message-circle"
          :is-loading="
            whatsappConversationPhone === participantPhone(participant)
          "
          :disabled="!!whatsappConversationPhone"
          @click="startWhatsappConversation(participant)"
        />
      </div>
    </div>
    <template v-else>
      <div class="flex justify-between">
        <div class="flex justify-between w-full mb-1">
          <div>
            <p v-if="watchersList.length" class="m-0 text-sm total-watchers">
              <Spinner v-if="watchersUiFlas.isFetching" size="tiny" />
              {{ totalWatchersText }}
            </p>
            <p v-else class="m-0 text-sm text-n-slate-10">
              {{ $t('CONVERSATION_PARTICIPANTS.NO_PARTICIPANTS_TEXT') }}
            </p>
          </div>
          <NextButton
            v-tooltip.left="$t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS')"
            slate
            ghost
            sm
            icon="i-lucide-settings"
            class="relative -top-1"
            :title="$t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS')"
            @click="onOpenDropdown"
          />
        </div>
      </div>
      <div class="flex items-center justify-between">
        <ThumbnailGroup
          :more-thumbnails-text="moreThumbnailsText"
          :show-more-thumbnails-count="showMoreThumbs"
          :users-list="thumbnailList"
        />
        <p v-if="isUserWatching" class="m-0 text-sm text-n-slate-10">
          {{ $t('CONVERSATION_PARTICIPANTS.YOU_ARE_WATCHING') }}
        </p>
        <NextButton
          v-else
          link
          xs
          icon="i-lucide-arrow-right"
          class="!gap-1"
          :label="$t('CONVERSATION_PARTICIPANTS.WATCH_CONVERSATION')"
          @click="onSelfAssign"
        />
      </div>
      <div
        v-on-clickaway="
          () => {
            onCloseDropdown();
          }
        "
        :class="{
          'block visible': showDropDown,
          'hidden invisible': !showDropDown,
        }"
        class="border rounded-lg shadow-lg bg-n-alpha-3 absolute backdrop-blur-[100px] border-n-strong dark:border-n-strong p-2 z-[9999] box-border top-8 w-full"
      >
        <div class="flex items-center justify-between mb-1">
          <h4
            class="m-0 overflow-hidden text-sm whitespace-nowrap text-ellipsis text-n-slate-12"
          >
            {{ $t('CONVERSATION_PARTICIPANTS.ADD_PARTICIPANTS') }}
          </h4>
          <NextButton
            ghost
            slate
            xs
            icon="i-lucide-x"
            @click="onCloseDropdown"
          />
        </div>
        <MultiselectDropdownItems
          :options="agentsList"
          :selected-items="selectedWatchers"
          has-thumbnail
          @select="onClickItem"
        />
      </div>
    </template>
  </div>
</template>
